import { EXTERNAL_SCOPE_TYPES } from '../models/messaging-user';
import { action } from '@ember/object';
import {
  conversationUserNames,
  firstNameAndLastNameInitial,
} from '../utils/messaging/conversation';
import { decodeText } from '../utils/messaging/format-text';
import { filterSystemAndCurrentUser } from '../utils/messaging/user';
import { on } from '@ember/modifier';
import { or } from 'ember-truth-helpers';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import AttachTooltip from 'ember-attacher/components/attach-tooltip';
import Avatar from './avatar.gjs';
import Component from '@glimmer/component';
import FormatMessagingTimestamp from '../helpers/format-messaging-timestamp';
import styles from './conversation-list-item.module.scss';

export default class ConversationListItem extends Component {
  @service messaging;
  @service messagingNavigation;
  @service intl;

  get isUnread() {
    return this.messaging.currentProfile.unreadConversationIds.includes(this.args.conversation.id);
  }

  get userNames() {
    const names = this.sortedFilteredUsers.map(user =>
      this.args.conversation?.isGroup ? firstNameAndLastNameInitial(user.name) : user.name
    );
    return conversationUserNames(names);
  }

  get userNamesTooltip() {
    const fullNames = this.sortedFilteredUsers.map(user => user.name);
    return conversationUserNames(fullNames);
  }

  get emptyConversationPlaceholder() {
    return this.intl.t('emc.start_conversation_callout', {
      name: this.userNames,
    });
  }

  get filteredUsers() {
    return filterSystemAndCurrentUser(
      this.args.conversation?.users,
      this.messaging.currentProfile?.user
    );
  }

  get sortedFilteredUsers() {
    const users = this.filteredUsers ?? [];
    return [...users].sort((a, b) =>
      (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })
    );
  }

  get clinicianUser() {
    return this.filteredUsers.find(user => user.externalScope === EXTERNAL_SCOPE_TYPES.user);
  }

  get showAwayIndicator() {
    return this.messaging.currentProfile?.isClient && this.clinicianUser?.isAway;
  }

  get showLastMessageAuthor() {
    return this.args.conversation?.isGroup || this.args.conversation?.isCouple;
  }

  get lastMessageAuthorPrefix() {
    const authorName = this.args.conversation?.lastMessage?.user?.name;
    if (!this.showLastMessageAuthor || !authorName) return '';
    return `${firstNameAndLastNameInitial(authorName)}: `;
  }

  @action
  openConversation() {
    this.messagingNavigation.toConversation(this.args.conversation);
  }

  <template>
    <li
      class="{{styles.component}} conversation-list-item"
      data-test-conversation={{@conversation.id}}
      {{on "click" this.openConversation}}
      role="button"
    >
      <Avatar
        @users={{this.sortedFilteredUsers}}
        @isGroup={{@conversation.isGroup}}
        @isCouple={{@conversation.isCouple}}
      />

      <div class="item-details">
        <div class="top-line">
          {{#if this.isUnread}}
            <span
              class="unread-dot"
              aria-label={{t "emc.unread_message_indicator"}}
              data-test-unread-indicator
            ></span>
          {{/if}}
          <span class="user-names" data-test-user-names>
            {{this.userNames}}
            {{#if (or @conversation.isGroup @conversation.isCouple)}}
              <AttachTooltip
                @placement="bottom"
                @arrow={{true}}
                @animation="fade"
                @class="emc-tooltip"
              >
                <span data-test-user-names-tooltip>{{this.userNamesTooltip}}</span>
              </AttachTooltip>
            {{/if}}
            {{#if this.showAwayIndicator}}
              <span class="away-indicator" data-test-away-indicator>
                {{t "emc.away_status.indicator"}}
              </span>
            {{/if}}
          </span>
          <span class="timestamp" data-test-timestamp>
            {{FormatMessagingTimestamp @conversation.updatedAt}}
          </span>
        </div>
        <p class="last-message" data-test-last-message>
          {{#if @conversation.hasMessageDraft}}
            <span class="draft-indicator" data-test-draft-indicator>
              {{t "emc.draft_indicator"}}
            </span>
            {{decodeText @conversation.messageDraft}}
          {{else if @conversation.lastMessage}}
            {{this.lastMessageAuthorPrefix}}{{decodeText @conversation.lastMessage.text}}
          {{else}}
            <span class="empty-conversation-callout">
              {{this.emptyConversationPlaceholder}}
            </span>
          {{/if}}
        </p>
      </div>
    </li>
  </template>
}
